import { Flame, TrendingUp, ArrowDownUp } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer } from "recharts";
import { formatPriceStr, formatUSD } from "../data/api";
import { PriceDisplay } from "./PriceDisplay";
import CONFIG from "../config";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-forge-surface border border-forge-steel p-3 font-mono text-xs" style={{ borderRadius: "2px" }}>
      <p className="text-muted-foreground mb-1">{label}</p>
      <PriceDisplay price={payload[0].value} className="text-fire-red font-bold" />
    </div>
  );
};

const fmt = (num) => {
  if (!num && num !== 0) return "0";
  if (num >= 1e12) return `${(num / 1e12).toFixed(2)}T`;
  if (num >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
  if (num >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
  if (num >= 1e3) return `${(num / 1e3).toFixed(1)}K`;
  return num.toLocaleString(undefined, { maximumFractionDigits: 0 });
};

export const BurnTracker = ({ stats, priceHistory, burnData, buybackTotalSol = 0 }) => {
  const priceChange = stats?.price_change_24h || 0;
  const price = stats?.price_usd || 0;
  const priceNative = stats?.price_native || 0;
  const burned = burnData?.burned || 0;
  const burnPct = burnData?.burnPercentage || 0;
  const currentSupply = burnData?.currentSupply || 0;
  const initialSupply = burnData?.initialSupply || 0;
  const solUsdPrice = priceNative > 0 ? price / priceNative : 0;
  const buybackUSD = buybackTotalSol * solUsdPrice;

  const chartData = (priceHistory || []).map((e) => ({
    name: new Date(e._ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    price: e.price_usd || 0,
  }));

  return (
    <div data-testid="burn-tracker" className="card-forge p-4 sm:p-6" style={{ borderRadius: "2px" }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-5 sm:mb-6">
        <div className="flex items-center gap-3">
          <div className="bg-fire-red/10 border border-fire-dark/50 p-2" style={{ borderRadius: "2px" }}>
            <Flame className="w-5 h-5 text-fire-red" />
          </div>
          <div>
            <h2 className="font-cinzel text-sm sm:text-base font-bold uppercase tracking-wider text-foreground">Burn & Buyback Tracker</h2>
            <p className="font-mono text-[10px] text-muted-foreground uppercase tracking-wider">On-chain Supply & Buyback Data</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <TrendingUp className={`w-4 h-4 ${priceChange >= 0 ? "text-green-500" : "text-red-400"}`} />
          <span className={`font-mono text-xs sm:text-sm font-bold ${priceChange >= 0 ? "text-green-500" : "text-red-400"}`}>
            {priceChange >= 0 ? "+" : ""}{priceChange.toFixed(2)}%
          </span>
        </div>
      </div>

      {/* Two-column layout: Burn | Buyback */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* LEFT: Burn */}
        <div>
          <div className="p-3 sm:p-4 bg-forge-deep border border-fire-dark/30 mb-4" style={{ borderRadius: "2px" }}>
            <div className="flex items-baseline gap-3 mb-3">
              <Flame className="w-4 h-4 text-fire-orange animate-fire-pulse flex-shrink-0 mt-1" />
              <div>
                <p className="font-mono text-[10px] text-fire-ember uppercase tracking-widest mb-1">Total Burned</p>
                <p data-testid="burn-amount" className="font-cinzel text-xl sm:text-2xl font-black text-fire-glow tracking-wide">
                  {fmt(burned)}
                </p>
              </div>
            </div>
            <div className="mt-3">
              <div className="flex justify-between mb-1.5">
                <span className="font-mono text-[10px] text-fire-ember">{burnPct.toFixed(2)}% Burned</span>
                <span className="font-mono text-[10px] text-muted-foreground">{fmt(initialSupply)} Total</span>
              </div>
              <div className="burn-bar-track h-4 sm:h-5" style={{ borderRadius: "2px" }}>
                <div className="burn-bar-fill h-full" data-testid="burn-progress-bar" style={{ width: `${Math.min(burnPct, 100)}%`, borderRadius: "2px" }} />
              </div>
            </div>
          </div>

          {/* Supply cards */}
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-forge-deep border border-forge-steel p-2 sm:p-3" style={{ borderRadius: "2px" }}>
              <p className="font-mono text-[9px] sm:text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Initial</p>
              <p className="font-mono text-xs sm:text-sm font-bold text-foreground">{fmt(initialSupply)}</p>
            </div>
            <div className="bg-forge-deep border border-forge-steel p-2 sm:p-3" style={{ borderRadius: "2px" }}>
              <p className="font-mono text-[9px] sm:text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Circulating</p>
              <p className="font-mono text-xs sm:text-sm font-bold text-foreground">{fmt(currentSupply)}</p>
            </div>
            <div className="bg-forge-deep border border-fire-dark/40 p-2 sm:p-3" style={{ borderRadius: "2px" }}>
              <p className="font-mono text-[9px] sm:text-[10px] text-fire-ember uppercase tracking-wider mb-1">Burned</p>
              <p className="font-mono text-xs sm:text-sm font-bold text-fire-red">{fmt(burned)}</p>
            </div>
          </div>
        </div>

        {/* RIGHT: Buyback + Chart */}
        <div>
          <div className="p-3 sm:p-4 bg-forge-deep border border-fire-dark/30 mb-4" style={{ borderRadius: "2px" }}>
            <div className="flex items-baseline gap-3 mb-1">
              <ArrowDownUp className="w-4 h-4 text-fire-orange flex-shrink-0 mt-1" />
              <div>
                <p className="font-mono text-[10px] text-fire-ember uppercase tracking-widest mb-1">Total Buyback</p>
                <p data-testid="buyback-amount" className="font-cinzel text-xl sm:text-2xl font-black text-fire-glow tracking-wide">
                  {buybackTotalSol > 0 ? `${buybackTotalSol.toFixed(4)} SOL` : "0 SOL"}
                </p>
              </div>
            </div>
            <p className="font-mono text-[10px] text-muted-foreground mt-2 ml-7">
              {formatUSD(buybackUSD)} value
            </p>
          </div>

          {/* Chart */}
          {chartData.length > 1 ? (
            <div data-testid="price-chart" className="h-[108px] sm:h-[124px]">
              <p className="font-mono text-[10px] text-muted-foreground uppercase tracking-wider mb-2">Price</p>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="priceGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#dc2626" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#dc2626" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="name" stroke="#57534e" tick={{ fontSize: 9, fontFamily: "'JetBrains Mono'" }} axisLine={false} tickLine={false} />
                  <YAxis stroke="#57534e" tick={{ fontSize: 9, fontFamily: "'JetBrains Mono'" }} axisLine={false} tickLine={false} tickFormatter={(v) => formatPriceStr(v)} domain={["auto", "auto"]} width={55} />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="price" stroke="#dc2626" strokeWidth={2} fill="url(#priceGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-[108px] sm:h-[124px] flex items-center justify-center border border-forge-steel/30 bg-forge-deep" style={{ borderRadius: "2px" }}>
              <p className="font-mono text-xs text-muted-foreground">Chart building... (snapshots every 30s)</p>
            </div>
          )}
        </div>
      </div>

      {/* Buyback wallet info */}
      <div className="mt-4 pt-3 border-t border-forge-steel/20 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] text-muted-foreground/50 uppercase tracking-wider">Buyback Wallet:</span>
          <a
            href={`https://solscan.io/account/${CONFIG.BUYBACK_WALLET}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="buyback-wallet-link"
            className="font-mono text-[10px] text-muted-foreground/40 hover:text-fire-orange transition-colors truncate max-w-[200px] sm:max-w-[320px]"
          >
            {CONFIG.BUYBACK_WALLET}
          </a>
        </div>
        <span className="font-mono text-[10px] text-muted-foreground/30">{CONFIG.TOKEN_SYMBOL}/SOL</span>
      </div>
    </div>
  );
};
